import { EXTERIOR_WORLD, exteriorPathZ, exteriorCreekZ } from "@catworld/shared";
import { exteriorGroundY } from "./exterior/ground.js";
/**
 * The single source of truth for where everything in the neighbourhood goes.
 *
 * Every level module reads its coordinates from here, so the fence line, the
 * driveway and the back gate agree without any of them knowing about each
 * other.  Change a number here and the whole street moves together.
 *
 * Axes follow the rest of the project: +Y up, metres.  Looking down from
 * above, +X runs east along the street and +Z runs south toward the road, so
 * the layout reads in bands from the greenspace at the back to the road at the
 * front:
 *
 *      -46  greenspace (trees, winding path)
 *      -30  back fence line
 *   -30..-11.5  back gardens, one per lot, fenced from each other
 *  -11.5..1.5   the three houses
 *    1.5..15    front gardens and driveways
 *      15..17   sidewalk
 *        17.3   kerb
 *    17.3..26   road (at x = 0; the crescent bows everything from the
 *               sidewalk out by up to ~10.6 m at the level edges)
 */

// What the cat can physically do.  Anything a module builds has to be
// reachable given these, or that part of the map is decoration the player can
// never touch.
export const MOVE = {
  jumpHeight: 0.95,      // apex above the surface jumped from
  stepHeight: 0.23,      // lip mounted at a walk, no jump needed.  Must match
                         // player.js STEP_HEIGHT: reach.mjs floods with this
                         // number, and at the old 0.13 it was modelling a
                         // clumsier cat than the game actually ships.
  shoulder: 0.27,        // the cat itself, for sense of scale
  halfWidth: 0.12,       // collision half-extent
};

export const LOT_WIDTH = 13.5;
// Lot spacing is house width + 2.0 m, so the gable walls sit about two metres
// apart, as they do in the reference street.  It was 3 m, which read as a
// much more generous subdivision than the photographs show.
export const LOT_X = [-13.5, 0, 13.5];          // centre of each lot
export const LOT_EDGES = [-20.25, -6.75, 6.75, 20.25];

export const Z = {
  greenBack: -46,        // far edge of the greenspace
  pathCentre: -37,       // the winding path through it
  backFence: -30,        // rear boundary of the gardens
  houseBack: -11.5,
  houseFront: 1.5,
  frontYard: 9.5,        // where the front gardens end
  walkNear: 7.95,
  walkFar: 9.35,         // sidewalk band
  kerb: 9.8,
  roadFar: 16.0,
};

// maxZ carries the road's far edge at the level corners: roadFar (21.5) plus
// the crescent's ~10.6 m of bow at x = +/-34.
export const BOUNDS = EXTERIOR_WORLD.bounds;

/**
 * The block slopes.
 *
 * Everything behind the houses -- gardens, greenspace, the wood -- sits 0.80 m
 * below the street, and the houses' back walls retain the difference.  That
 * drop is the whole reason the hero's deck is a raised one: the deck top is
 * level with the kitchen floor and you step straight out onto it, while from
 * the lawn below it stands the 1.35 m it was photographed at.
 *
 * The line sits 0.10 m behind Z.houseBack so a house's own back plinth, its
 * door surround and the boards of a deck butted against it all stay on the
 * upper side of it and cannot be caught half-way down the step.
 */
export const GRADE = { drop: 0.80, line: Z.houseBack - 0.10 };

/**
 * The ramp that carries the street down to the garden terrace.
 *
 * The drop used to be a wall at GRADE.line with a flight of steps in each
 * side passage.  It is now a slope that starts at the houses' front elevation
 * and reaches full depth at their back wall -- 0.80 m over 13.1 m, a 6%
 * average -- so a lot reads as built into a hillside rather than onto a
 * shelf.  The side passages are ramps now, not stairs.
 *
 * Smoothstep rather than a straight line on purpose: a linear ramp creases at
 * both ends, and a crease across a lawn catches the light as a fold.  This is
 * tangent to both terraces, at the cost of a 9% maximum in the middle.
 */
const RAMP_TOP = Z.houseFront;      //   1.5, crest
const RAMP_TOE = GRADE.line;        // -11.6, full depth

const smoothstep = (t) => t * t * (3 - 2 * t);

/** Height of the terrace surface at z, ignoring anything cut into it. */
export function terraceY(z) {
  if (z >= RAMP_TOP) return 0;
  if (z <= RAMP_TOE) return -GRADE.drop;
  return -GRADE.drop * smoothstep((RAMP_TOP - z) / (RAMP_TOP - RAMP_TOE));
}

/**
 * The creek through the greenspace.
 *
 * The centre line lives here rather than in greenspace.js because groundY has
 * to cut the channel, and the level module has to lay its water in the same
 * place -- one of them owning it and the other guessing is how a creek ends
 * up beside its own bed.
 *
 * The line is the trail's, pushed back 2.4..3.6 m with a slower wobble of its
 * own, so the two converge and part without ever touching.  The soft floor is
 * a log-sum-exp max, which keeps the line smooth where a hard clamp would put
 * a corner in it -- and a corner in the centre line is a corner in the
 * terrain, which is much more visible than a corner in a painted ribbon was.
 */
const CREEK_FLOOR = -43.8;
export function creekZ(x) { return exteriorCreekZ(x); }

/**
 * The channel itself.
 *
 * `depth` and `halfWidth` are the values at the deepest pools; `reach()` below
 * scales both along the creek's length.  `depth` is measured down from the
 * surrounding lawn and `halfWidth` out to where the bank meets it.  Smoothstep again, which buys both ends at once:
 * zero slope at the rim, so there is no lip for the cat to catch on and no
 * crease around the whole creek, and zero slope at the centre, so the bed is
 * flat enough to stand in.  The steepest part is the middle of the bank at
 * 1.5 * depth/halfWidth = 36%, about 20 degrees -- walked in and out at any
 * angle, which is what stops the creek being a trap.
 *
 * `waterY` is absolute: the greenspace terrace, less the cut, plus the depth
 * of water standing in it.  It is a plane, not a ribbon -- the shoreline is
 * wherever the terrain rises through it, so the creek widens in the shallows
 * and pinches over the bars without anyone drawing that.
 */
export const CREEK = {
  halfWidth: 1.75,
  depth: 0.42,
  waterDepth: 0.15,
  get waterY() { return -GRADE.drop - this.depth + this.waterDepth; },
};

/**
 * How far along its length the creek is a pool and how far a riffle.
 *
 * A channel of constant depth is a channel of constant *wetted width*, which
 * puts the shoreline back to being a ribbon of fixed width -- the exact fault
 * the carved bed was meant to cure.  Cutting deeper in some reaches than
 * others is what makes the waterline move: the water plane is flat, so
 * wherever the bed rises toward it the water pinches to a trickle over the
 * gravel, and wherever it falls away the pool spreads to the banks.
 *
 * Bottoms out at 0.72 rather than going to nothing on purpose.  Below about
 * 0.64 the bed breaks the surface and the creek becomes a chain of ponds with
 * dry gaps -- which is a real thing a creek does in August, and completely
 * wrong for one the player is meant to be able to follow and wade along.
 */
function reach(x) {
  const slow = 0.5 + 0.5 * Math.sin(x / 7.9 + 0.6);
  const fast = 0.5 + 0.5 * Math.sin(x / 3.3 + 2.4);
  return 0.72 + 0.22 * slow + 0.06 * fast;        // 0.72 .. 1.00
}

/** Depth of the cut at the centre line, and its half-width there. */
export const creekDepthAt = (x) => CREEK.depth * reach(x);
export const creekHalfWidthAt = (x) => CREEK.halfWidth * (0.82 + 0.18 * reach(x));

/** How far the ground is cut below the terrace at a point.  0 outside. */
export function creekCut(x, z) {
  const d = Math.abs(z - creekZ(x)) / creekHalfWidthAt(x);
  if (d >= 1) return 0;
  return creekDepthAt(x) * smoothstep(1 - d);
}

/**
 * Ground height at a point: the one function the player, the level and the
 * reachability tools all read, so none of them can disagree about it.
 *
 * This is the *walkable surface*, and it is no longer flat anywhere in
 * particular.  Two consequences worth knowing before building against it:
 *
 *   - There is no maximum walkable gradient.  The player floors against this
 *     value unconditionally, so the cat climbs whatever this returns, however
 *     steep.  Terrain can therefore never be used as a barrier -- that still
 *     needs a collider -- and equally the cat can never be trapped by it.
 *   - It is sampled per piece by the Builder, at each piece's centre.  A
 *     piece sits ON the terrain; it does not tilt WITH it.  Anything long
 *     enough that the ends disagree has to be built in sections.
 */
export const groundY = exteriorGroundY;

/**
 * The level pad a built structure stands on.
 *
 * Not the same thing as groundY, and the difference is the whole reason both
 * exist.  A house on a slope is not tilted: it is set on a bench cut level
 * into the hill, and the ground falls away past its walls.  benchY is that
 * bench -- the old two-terrace step -- so the houses, their floors, their
 * porches and the fences between them stay square while the lawn ramps
 * underneath.
 *
 * Grading the houses by groundY instead would drop each one by the depth of
 * the ramp at its own centre, taking the porch, the interior floor and the
 * front steps with it.
 */
export const benchY = (x, z) => (z < GRADE.line ? -GRADE.drop : 0);

/**
 * Which level each module builds on.
 *
 *   0        the street's level -- the default, so most modules say nothing
 *   number   the module sits entirely on one terrace
 *   benchY   it spans both, on a level pad either side of the change
 *   groundY  it follows the terrain, piece by piece
 *   null     it works in absolute y and handles the change itself
 *
 * This lives in the spec rather than in the level builder because the
 * headless tools assemble the same modules themselves: a policy kept in
 * buildLevel would leave inspect, reach and walk each modelling a different
 * world, and a reachability report about a world nobody plays is worse than
 * none.
 */
export const MODULE_GRADE = {
  ground: null,               // builds the terrain surface itself
  hero: null,                 // hero/index.js grades its own parts
  houses: benchY,             // back steps are down the slope, houses are not
  fences: benchY,             // panels are level; a run steps down the ramp
  yards: benchY,
  greenspace: groundY,        // trees, rocks and reeds follow the ground
  "hero/garden": -GRADE.drop,
  "hero/deck": -GRADE.drop,
  "hero/patio": -GRADE.drop,
  "hero/shed": -GRADE.drop,
};

export const gradeFor = (name) =>
  name in MODULE_GRADE ? MODULE_GRADE[name] : 0;

/**
 * Proportions taken off the street-level reference: two storeys of beige
 * brick, a steep charcoal shingle roof with a gable facing the street, a
 * covered porch on square columns, and a single-car garage built into the
 * front elevation.
 */
export const HOUSE = {
  width: 11.5,
  depth: 13,
  storey: 3.1,
  wallHeight: 6.2,       // two storeys
  ridgeHeight: 3.6,      // above the wall top; steep, as in the photo
  eaveOverhang: 0.5,
  foundation: 0.55,      // brick plinth the whole house sits on
  garageWidth: 3.6,      // single car, on the +X side of each house
  garageHeight: 2.6,
  porchWidth: 4.4,       // covered entry on the -X side
  porchDepth: 2.2,
  porchHeight: 3.0,
  doorWidth: 1.15,
  doorHeight: 2.35,
  windowSill: 1.0,       // ground-floor sill above the floor
  stepRise: 0.17,        // porch steps
};

/** House colours vary slightly per lot, as they do along a real street. */
export const LOT_STYLE = [
  { brick: "brickWarm", door: "doorRed", drive: "asphalt" },
  { brick: "brick", door: "doorTan", drive: "concrete" },
  { brick: "brickPale", door: "doorGreen", drive: "asphalt" },
];

/**
 * The crescent.  The road, kerb and sidewalk all bend together; the houses
 * stay square to the world, exactly as they do in the reference photo.
 *
 * A circular arc -- the honest shape for a road: offset 0 at x = 0 rising to
 * ~10.6 m at the level edges (x = +/-34).  R = 60 m is a plausible
 * centre-line radius for a slow suburban crescent, bows the street deeply
 * enough to read unmistakably from the ground and the air, and keeps the
 * tangent at the map corners (~35 deg) shallow enough for the ribbon and
 * kerb builders to follow.  The arc's minimum is at x = 0, so the middle
 * lot -- the red-doored house -- sits on the apex, closest to the road.
 */
const CURVE_R = 65;
export function roadCurve(x) {
  return -(CURVE_R - Math.sqrt(CURVE_R * CURVE_R - x * x));
}

/** Centre line of the winding greenspace path. */
export function pathCurve(x) {
  return 3.2 * Math.sin(x / 11) + 0.9 * Math.sin(x / 4.5 + 1.1);
}

/** Z of the road centre line at a given x. */
export const roadCentreZ = (x) => (Z.kerb + Z.roadFar) / 2 + roadCurve(x);
/** Z of the kerb (front edge of the road) at a given x. */
export const kerbZ = (x) => Z.kerb + roadCurve(x);
/** Z of the sidewalk's two edges at a given x. */
export const walkNearZ = (x) => Z.walkNear + roadCurve(x);
export const walkFarZ = (x) => Z.walkFar + roadCurve(x);
/**
 * Z of the front property line: where a lot's frontage stops and the public
 * verge begins, half a metre back from the sidewalk so a boundary planted on
 * it does not overhang the pavement.
 *
 * A setback from walkNearZ and not a constant, which is the whole point.  The
 * crescent bows the sidewalk out by nearly 4 m between the middle of the
 * street and the level corners, so anything along the frontage written as a
 * fixed z lands correctly at exactly one x and drifts everywhere else -- the
 * front pickets, the hedges and lot 2's ornamental bed were all authored flat
 * at 13.0-13.55, which by the time the crescent went in had put the pickets on
 * the pavement and the hedge in the road.
 *
 * Things that ARE the boundary follow this line.  Things that merely belong
 * behind it should clamp to it; see insideFront.
 */
export const frontLineZ = (x) => walkNearZ(x) - 0.55;

/**
 * `z`, but never closer to the street than `clearance` inside the front line.
 *
 * For planting that has an authored spot worth keeping: a piece already well
 * inside its lot is returned untouched, and only the ones the curve pushed out
 * over the pavement get pulled back.  `clearance` is the piece's own reach --
 * a tree's mulch ring, a bed's stone edge -- so it is the whole prop that
 * stays inside the line, not just its centre.
 */
export const insideFront = (x, z, clearance = 0) =>
  Math.min(z, frontLineZ(x) - clearance);

/** Z of the greenspace path centre at a given x. */
export const greenPathZ = exteriorPathZ;

/**
 * The middle house is rebuilt in detail from a street photograph, so the
 * generic house/yard/path generators skip it and `hero/` supplies its front
 * elevation, porch and planting instead.
 */
export const HERO_LOT = 1;

/**
 * Where the player starts: on the front walk directly outside the middle
 * house's front door, facing the street -- the cat has just been let out.
 *
 * Deliberately at grade rather than up on the porch: the porch deck sits under
 * the porch roof, and anything that asks "what can be stood on here" gets the
 * roof, not the deck.
 */
export const SPAWN = EXTERIOR_WORLD.spawn;

/** Handy per-lot geometry, so modules do not re-derive it. */
export function lot(i) {
  const cx = LOT_X[i];
  return {
    index: i,
    centreX: cx,
    minX: cx - LOT_WIDTH / 2,
    maxX: cx + LOT_WIDTH / 2,
    house: {
      minX: cx - HOUSE.width / 2,
      maxX: cx + HOUSE.width / 2,
      minZ: Z.houseBack,
      maxZ: Z.houseFront,
      garageX: cx + HOUSE.width / 2 - HOUSE.garageWidth / 2 - 0.3,
      doorX: cx - HOUSE.width / 2 + 2.6,
    },
  };
}

export const LOTS = [0, 1, 2].map(lot);

/**
 * The car parked on the middle driveway.
 *
 * Two places need to agree on it and neither owns it: road.js lays the
 * colliders the cat climbs, the client's car.js draws the boxes.  Get them
 * out of step and the cat walks on an invisible car a metre from the visible
 * one -- which is exactly why both now read CAR_TIERS below instead of
 * keeping their own copy of the shape.
 *
 * Parked nose-in toward the garage.  That pose is baked into CAR_TIERS
 * itself -- the tier offsets run nose-to-tail along -z to +z, already in the
 * street's axes -- so there is no yaw here any more: the model that needed a
 * half turn (it came out of its file facing +z) went with the .glb.  `y` is
 * the driveway slab (road.js Y.drive), not the earth, so the car sits on the
 * concrete rather than 15 mm inside it.
 */
export const CAR = {
  x: 3.35,
  z: 4.6,                // centre of a 4.72 m car: nose 2.26, tail 6.98
  y: 0.082,
};

/**
 * The car's shape, as the stack of tiers both its consumers build from:
 * road.js stands a collider on every tier, car.js draws a box on every tier,
 * and because this is the one copy the visible car and the collided car
 * cannot drift.
 *
 * The numbers were traced off the Tesla Model 3 model this prop used to be --
 * its height and width sampled in 8 cm slices down its length -- and they
 * outlive it: the model is gone (it was a login-gated Sketchfab download no
 * fresh clone could fetch; docs/ASSETS.md tells that story), the silhouette
 * it left behind is the car now.  Read nose-back the shape is a staircase:
 *
 *   ground -> 0.58 bumper      a jump, and the only one needed
 *          -> 0.66 -> 0.75 -> 0.84 -> 0.92    up the nose and over the bonnet
 *          -> 0.99 cowl
 *          -> 1.11 -> 1.22 -> 1.33 -> 1.40    up the windscreen
 *          -> 1.44 roof
 *          -> 1.40 -> 1.34 -> 1.27 -> 1.19    down the back glass
 *          -> 1.08 boot lid
 *
 * No rise is over 0.12, half of MOVE.stepHeight, so the cat walks the whole
 * car end to end at a stroll.  Every tier starts at the ground rather than
 * resting on the one below: as colliders a stack of floating lids would
 * leave pockets for the cat to walk into (the cowl under the windscreen
 * especially), and as boxes the union looks the same either way.  Widths
 * taper with the body and never carried door mirrors.
 *
 * z0/z1 are metres from the car's centre with the nose toward -z, toward
 * the garage; `top` is the tier's height off the ground, `w` its full width.
 * `part` is what the surface is made of, for the renderer's palette --
 * "sill" (the rockers-bumpers-wheels band), "body" (painted metal), or
 * "glass" (the windscreen-roof-backlight canopy; a Model 3's roof really is
 * glass).  carColliders ignores it: every tier collides the same.
 *
 * @type {{z0: number, z1: number, top: number, w: number,
 *         part: "sill" | "body" | "glass"}[]}
 */
export const CAR_TIERS = [
  { z0: -2.38, z1:  2.42, top: 0.58, w: 1.86, part: "sill" },   // sills, bumpers, wheels: under everything
  { z0: -2.38, z1: -2.20, top: 0.66, w: 1.00, part: "body" },   // nose tip
  { z0: -2.20, z1: -1.95, top: 0.75, w: 1.50, part: "body" },
  { z0: -1.95, z1: -1.70, top: 0.84, w: 1.74, part: "body" },   // over the wings
  { z0: -1.70, z1: -1.30, top: 0.92, w: 1.83, part: "body" },   // bonnet
  { z0: -1.30, z1: -1.05, top: 0.99, w: 1.70, part: "body" },   // cowl, at the foot of the screen
  { z0: -1.05, z1:  2.30, top: 0.99, w: 1.84, part: "body" },   // beltline: door tops and rear haunches
  { z0: -1.05, z1: -0.80, top: 1.11, w: 1.52, part: "glass" },  // windscreen, in four
  { z0: -0.80, z1: -0.58, top: 1.22, w: 1.50, part: "glass" },
  { z0: -0.58, z1: -0.36, top: 1.33, w: 1.44, part: "glass" },
  { z0: -0.36, z1: -0.14, top: 1.40, w: 1.40, part: "glass" },
  { z0: -0.14, z1:  1.00, top: 1.44, w: 1.34, part: "glass" },  // roof
  { z0:  1.00, z1:  1.25, top: 1.40, w: 1.28, part: "glass" },  // back glass, in four
  { z0:  1.25, z1:  1.50, top: 1.34, w: 1.28, part: "glass" },
  { z0:  1.50, z1:  1.75, top: 1.27, w: 1.30, part: "glass" },
  { z0:  1.75, z1:  2.00, top: 1.19, w: 1.30, part: "glass" },
  { z0:  2.00, z1:  2.36, top: 1.08, w: 1.44, part: "body" },   // boot lid, out to its lip
];
